import billingPolicy from './config/billingPolicy.json';
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "./utils/firebase";
import { useAuth } from "./utils/authContext";
import { AppShellProvider, useAppShellContext } from "./contexts/AppShellContext";
import { useStreaming } from "./contexts/StreamingContext";
import { useNetworkNotifications } from "./utils/networkNotifications";
import { useRecordingSession } from "./hooks/useRecordingSession";
import { useProcessingListener } from "./hooks/useProcessingListener";
import { useActiveJobsContext, ActiveJobsProvider } from "./contexts/ActiveJobsContext";
import { UnifiedNotificationProvider } from "./contexts/UnifiedNotificationContext";
import { useDocumentTypeSelection } from "./utils/documentTypeSelection";
import { canPerformAction } from "./utils/sharingService";
import { showNotification } from "./utils/notificationSystem";
import { setUserbackPosition } from "./utils/userback";
import { detectContext, AUTO_OPEN_CONTEXTS } from "./utils/activityBarContext";
import { SESSION_STATE } from "./utils/sessionTimeout";
import AppHeader from "./components/shell/AppHeader";
import AppSidebar from "./components/shell/AppSidebar";
import ActivityBar from "./components/activity/ActivityBar";
import AddEditPatient from "./utils/AddEditPatient";
import SessionBanner from "./components/SessionBanner";
import { performLogout } from "./components/shell/UserMenu";
import PriorityNotificationPopup from "./components/notifications/PriorityNotificationPopup";
import Tooltip from "@mui/material/Tooltip";
import { CircleArrowLeft, CircleArrowRight, Menu } from "lucide-react";
import "./css/AppShell.css";
import "./css/ActivityBar.css";
import "./css/session-banner.css";
import "./css/clinical-day-view.css";

/**
 * AppShell — Three-zone layout with Activity Bar (Phase 2).
 *
 * Loads user data and owns the authenticated layout.
 * Wraps children in AppShellProvider for sidebar + activity bar state.
 *
 * Zones: Header (top) | Activity Bar (bottom or right) | Sidebar (left) | Content (main)
 *
 * See: docs/features/infrastructure/app-shell.md
 */
const AppShellInner = ({ children }) => {
    const { currentUser, signOut, sessionState, graceTimeRemaining, warningTimeRemaining, setActiveOperation, extendSessionOnAction } = useAuth();
    const {
        sidebarMode,
        toggleSidebar,
        activityBarPosition,
        activityBarOpen,
        openActivityBar,
        closeActivityBar,
        closeIfImplicit,
        toggleActivityBar,
        selectedPatientId,
        selectedPatientName,
        activityBarDismissed,
    } = useAppShellContext();
    const { isStreaming, setIsStreaming } = useStreaming();
    const { networkStatus } = useNetworkNotifications();
    const navigate = useNavigate();
    const location = useLocation();
    const { patientId } = location.state || {};
    const userId = currentUser?.uid;

    const handleReauthenticate = useCallback(
        () => performLogout(signOut, navigate),
        [signOut, navigate]
    );

    // AppShell owns its processing state.
    const [isProcessing, setIsProcessing] = useState(false);

    // Phase 12: Active jobs tracking for ProcessingPill (replaces lock mechanism)
    const { activeJobs, dismiss: dismissJob } = useActiveJobsContext();

    // Derive hasActiveJobs early for effects
    const hasActiveJobs = activeJobs.length > 0;

    // Clinical Day View: inline Activity Bar on dashboard.
    const isDashboardRoute = location.pathname === "/dashboard" || location.pathname === "/";
    const inlineDashboard = isDashboardRoute;
    const isAdminRoute = location.pathname.startsWith("/admin") || location.pathname.startsWith("/company-admin");

    // Determine current page context for activity bar
    const currentPageContext = detectContext(location.pathname, selectedPatientId);

    // Auto-open activity bar on pages where it's needed (unless user dismissed it)
    // Tracks the last context that triggered auto-open to avoid re-firing on the same page
    const lastAutoOpenContextRef = useRef(null);
    useEffect(() => {
        // Skip if streaming (force-open handles that), admin routes, or inline dashboard
        if (isStreaming || isAdminRoute || inlineDashboard) return;
        // Skip contexts that don't need auto-open
        if (!AUTO_OPEN_CONTEXTS.has(currentPageContext)) return;
        // Skip if user has dismissed the bar for this context
        if (activityBarDismissed[currentPageContext]) return;
        // Don't re-open if we already auto-opened for this same context
        if (lastAutoOpenContextRef.current === currentPageContext && activityBarOpen) return;

        if (!activityBarOpen) {
            openActivityBar();
            lastAutoOpenContextRef.current = currentPageContext;
        }
    }, [currentPageContext, isAdminRoute, inlineDashboard]); // eslint-disable-line react-hooks/exhaustive-deps

    // Clear auto-open tracker when bar is closed
    useEffect(() => {
        if (!activityBarOpen) lastAutoOpenContextRef.current = null;
    }, [activityBarOpen]);

    // Sync active operation state to auth (bridges provider gap)
    useEffect(() => {
        setActiveOperation(isStreaming || isProcessing || hasActiveJobs);
    }, [isStreaming, isProcessing, hasActiveJobs, setActiveOperation]);

    // Keep Userback widget position in sync with Activity Bar position/state/size
    useEffect(() => {
        setUserbackPosition(activityBarPosition, activityBarOpen);
    }, [activityBarPosition, activityBarOpen]);

    // Session state transition notifications
    const lastNotifiedStateRef = useRef(null);
    useEffect(() => {
        if (!sessionState || sessionState === lastNotifiedStateRef.current) return;
        lastNotifiedStateRef.current = sessionState;

        if (sessionState === SESSION_STATE.WARNING) {
            showNotification("Your session expires in 30 minutes", "warning", 8000, { key: "session-warning" });
        } else if (sessionState === SESSION_STATE.GRACE) {
            showNotification("Session expired. Save your work — you have 2 hours.", "warning", 10000, { key: "session-grace" });
        }
    }, [sessionState]);

    // Load the current user's profile.
    const [userData, setUserData] = useState(null);
    const [uploadDocId, setUploadDocId] = useState(null);

    // Phase 12: Lock mechanism removed — backend owns patient/doc context post-submission.
    // Processing state is tracked by useActiveJobs / ProcessingPill in AppHeader.

    // Add Patient modal (Phase 5: moved from AppHeader to AppShell)
    const [isEditPatientOpen, setEditPatientOpen] = useState(false);
    const [editPatientId, setEditPatientId] = useState(null);

    // Wrap setEditPatientOpen to clear patientId when closing
    const handleSetEditPatientOpen = (open) => {
        setEditPatientOpen(open);
        if (!open) setEditPatientId(null);
    };

    // Listen for addPatient and editPatient action handler events
    useEffect(() => {
        const handleOpenAddPatient = () => {
            setEditPatientId(null);
            setEditPatientOpen(true);
        };
        const handleOpenEditPatient = (e) => {
            const pid = e.detail?.patientId ?? null;
            setEditPatientId(pid);
            setEditPatientOpen(true);
        };
        const handlePatientCreated = (e) => {
            const newId = e.detail?.patientId;
            if (newId && navigate) {
                navigate("/patient", { state: { patientId: newId } });
            }
        };
        window.addEventListener("openAddPatient", handleOpenAddPatient);
        window.addEventListener("openEditPatient", handleOpenEditPatient);
        window.addEventListener("patientCreated", handlePatientCreated);
        return () => {
            window.removeEventListener("openAddPatient", handleOpenAddPatient);
            window.removeEventListener("openEditPatient", handleOpenEditPatient);
            window.removeEventListener("patientCreated", handlePatientCreated);
        };
    }, [navigate]);

    useEffect(() => {
        const fetchUserData = async () => {
            if (userId) {
                try {
                    const userDoc = await getDoc(doc(db, "users", userId));
                    if (userDoc.exists()) {
                        setUserData(userDoc.data());
                    }
                } catch (error) {
                    console.error("[AppShell] Error fetching user data:", error);
                    setUserData({ userRole: "standard" });
                }
            }
        };
        fetchUserData();
    }, [userId]);

    // Billing portal handler.
    const handleManageBilling = async () => {
        if (!billingPolicy.enabled || !userData?.stripeCustomerId) return;
        try {
            const response = await fetch("/api/stripe/create-portal-session", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    customerId: userData.stripeCustomerId,
                    returnUrl: window.location.href,
                }),
            });
            const data = await response.json();
            if (data.url) window.location.href = data.url;
        } catch (error) {
            console.error("[AppShell] Error opening billing portal:", error);
        }
    };

    // Patient ID resolution — no more processing lock, always use current context
    const effectivePatientId = patientId || (inlineDashboard ? selectedPatientId : null);

    // Document type selection (shared between recording and upload panels)
    const dropdownState = useDocumentTypeSelection(effectivePatientId);

    // Recording session hook
    const session = useRecordingSession({
        patientId: effectivePatientId,
        userId,
        isStreaming,
        setIsStreaming,
        navigate,
        setIsProcessing,
    });

    // Phase 12: useActiveJobs and hasActiveJobs moved earlier in component (before effects that use them)

    // Firestore processing listener
    // Watches recording doc OR upload doc — whichever is active
    const activeDocId = session.documentId || uploadDocId;

    // Capture the patient name + id at the moment a doc starts processing, keyed by docId.
    // Prevents the completion toast from showing the currently-viewed patient when the user
    // has switched away from the originally-submitted patient during processing (Monday #11941010916).
    const submittedDocContextRef = useRef({});
    useEffect(() => {
        if (!activeDocId || submittedDocContextRef.current[activeDocId]) return;
        const fn = dropdownState?.patientData?.firstName;
        const ln = dropdownState?.patientData?.lastName;
        const name = (fn && ln) ? `${fn} ${ln}` : (selectedPatientName || "");
        submittedDocContextRef.current[activeDocId] = {
            patientId: effectivePatientId,
            patientName: name,
        };
    }, [activeDocId, dropdownState, selectedPatientName, effectivePatientId]);

    const processing = useProcessingListener({
        patientId: effectivePatientId,
        documentId: activeDocId,
        callbacks: {
            onContinue: () => {
                setIsProcessing(false);
                // Bell notification from backend is sufficient — no toast needed
            },
            onComplete: () => {
                setIsProcessing(false);
                const docIdForNav = activeDocId;
                const ctx = (docIdForNav && submittedDocContextRef.current[docIdForNav]) || {};
                const name = ctx.patientName || "Patient";
                const pidForNav = ctx.patientId || effectivePatientId;
                showNotification(
                    `Processing complete for ${name}`,
                    "success",
                    8000,
                    {
                        key: `processing-complete-${docIdForNav || "unknown"}`,
                        priority: true,
                        onClick: docIdForNav && pidForNav
                            ? () => navigate("/document", { state: { docId: docIdForNav, patientId: pidForNav, from: "/dashboard" } })
                            : undefined,
                    }
                );
            },
            onError: () => {
                setIsProcessing(false);
                const docIdForNav = activeDocId;
                const ctx = (docIdForNav && submittedDocContextRef.current[docIdForNav]) || {};
                const name = ctx.patientName || "Patient";
                showNotification(
                    `Processing failed for ${name}`,
                    "error",
                    8000,
                    { key: "processing-error" }
                );
            },
        },
    });


    // Determine if this is a view-only shared patient
    const isViewOnly = session.shareInfo &&
        !canPerformAction(session.shareInfo, "add");

    // Navigate to a document from notification bell
    const handleNavigateToDocument = useCallback((notifPatientId, docId) => {
        navigate("/document", { state: { docId, patientId: notifPatientId, from: "/dashboard" } });
    }, [navigate]);

    // Navigate to a patient (no doc) — used for share_received notifications
    const handleNavigateToPatient = useCallback((notifPatientId) => {
        if (!notifPatientId) return;
        navigate("/patient", { state: { patientId: notifPatientId, from: "/dashboard" } });
    }, [navigate]);

    // Header toggle wraps toggleActivityBar with current page context
    const handleToggleActivityBar = useCallback(() => {
        toggleActivityBar(currentPageContext);
    }, [toggleActivityBar, currentPageContext]);

    // Header action handler — opens Activity Bar with appropriate action
    const handleFunctionHeader = useCallback((option) => {
        if (isStreaming) return;
        openActivityBar(option);
    }, [isStreaming, openActivityBar]);

    // Listen for openRecordHeader event (dispatched by Patient.js when navigating from Dashboard mic)
    useEffect(() => {
        const handleOpenRecordHeader = () => {
            if (effectivePatientId && !isStreaming) {
                handleFunctionHeader("record");
            }
        };
        window.addEventListener("openRecordHeader", handleOpenRecordHeader);
        return () => window.removeEventListener("openRecordHeader", handleOpenRecordHeader);
    }, [effectivePatientId, isStreaming, handleFunctionHeader]);

    // Force activity bar open during streaming (processing is non-blocking)
    useEffect(() => {
        if (isStreaming) {
            if (!activityBarOpen) {
                openActivityBar("record");
            }
        }
    }, [isStreaming]);

    // Close activity bar when streaming stops (if opened implicitly)
    const wasStreamingRef = useRef(false);
    useEffect(() => {
        // Detect isStreaming transitioning from true to false
        if (wasStreamingRef.current && !isStreaming) {
            closeIfImplicit();
        }
        wasStreamingRef.current = isStreaming;
    }, [isStreaming, closeIfImplicit]);

    // Auto-close activity bar on pages that don't use it (admin, settings, generic)
    // unless an active recording is in progress.
    useEffect(() => {
        if (activityBarOpen && !isStreaming && !AUTO_OPEN_CONTEXTS.has(currentPageContext)) {
            closeActivityBar();
        }
    }, [location.pathname, currentPageContext]); // eslint-disable-line react-hooks/exhaustive-deps

    // Patient name derived from dropdown state (for Activity Bar header takeover)
    const patientName = dropdownState?.patientData?.firstName && dropdownState?.patientData?.lastName
        ? `${dropdownState.patientData.firstName} ${dropdownState.patientData.lastName}`
        : selectedPatientName || "";

    // Phase 12: Activity lock removed — processing context now managed by useActiveJobs/ProcessingPill

    // Back navigation handler (for Activity Bar Zone 0)
    const handleActivityBarBack = useCallback(() => {
        if (!isStreaming) {
            const from = location.state?.from;
            if (location.pathname === '/patient') {
                navigate(from || '/patients');
            } else {
                navigate(-1);
            }
        }
    }, [isStreaming, navigate, location.pathname, location.state]);

    // Determine grid class based on activity bar position/state
    // When inline dashboard is active, skip the grid activity zone — ActivityBar renders inside <main>
    let gridClass = "app-shell";
    if (activityBarOpen && !inlineDashboard) {
        gridClass += ` app-shell-with-activity-${activityBarPosition}`;
    }

    // Shared ActivityBar props
    const activityBarProps = {
        patientId: effectivePatientId,
        isStreaming,
        setIsStreaming,
        isProcessing,
        setIsProcessing,
        userId,
        navigate,
        session,
        processing,
        dropdownState,
        networkStatus,
        onUploadDocCreated: setUploadDocId,
        uploadDocId,
        isViewOnly,
        hasUnsavedChanges: false, // TODO: implement AppShell-native unsaved changes tracking if needed
        actionUsage: userData?.settings?.actionUsage || {},
        // Phase 6: Header takeover props
        patientName,
        lockedContext: null,
        onBack: handleActivityBarBack,
        // Phase 12: Bridge gap between submission and ProcessingPill
        hasActiveJobs,
    };

    return (
        <div className={gridClass}>
            {/* Header Zone */}
            <AppHeader
                isStreaming={isStreaming}
                isActivityBarOpen={activityBarOpen}
                onToggleActivityBar={handleToggleActivityBar}
                userId={userId}
                onNavigateToDocument={handleNavigateToDocument}
                onNavigateToPatient={handleNavigateToPatient}
                hideToggle={inlineDashboard || isAdminRoute}
                userData={userData}
            />

            {/* Sidebar Zone */}
            <AppSidebar
                userData={userData}
                onManageBilling={handleManageBilling}
            />

            {/* Collapse / Expand toggle — straddles sidebar edge */}
            <Tooltip
                title={sidebarMode === "full" ? "Collapse sidebar" : sidebarMode === "thin" ? "Open sidebar" : "Expand sidebar"}
                arrow
                placement="right"
            >
                <button
                    className="app-shell-collapse-btn"
                    onClick={toggleSidebar}
                    aria-label={sidebarMode === "full" ? "Collapse sidebar" : "Expand sidebar"}
                >
                    {sidebarMode === "thin" ? (
                        <Menu size={16} />
                    ) : sidebarMode === "full" ? (
                        <CircleArrowLeft size={16} />
                    ) : (
                        <CircleArrowRight size={16} />
                    )}
                </button>
            </Tooltip>

            {/* Activity Bar Zone (grid position) — only when NOT inline dashboard */}
            {activityBarOpen && !inlineDashboard && (
                <ActivityBar {...activityBarProps} />
            )}

            {/* Content Zone */}
            <main className={`app-shell-content${inlineDashboard ? " app-shell-content-inline-dashboard" : ""}`}>
                {sessionState === SESSION_STATE.WARNING && (
                    <SessionBanner
                        variant="warning"
                        timeRemaining={warningTimeRemaining}
                        onExtend={extendSessionOnAction}
                    />
                )}
                {(sessionState === SESSION_STATE.GRACE || sessionState === SESSION_STATE.GRACE_URGENT) && (
                    <SessionBanner
                        graceTimeRemaining={graceTimeRemaining}
                        isUrgent={sessionState === SESSION_STATE.GRACE_URGENT}
                        onReauthenticate={handleReauthenticate}
                    />
                )}
                {inlineDashboard ? (
                    <div className="clinical-day-view-layout">
                        <div className="clinical-day-view-activity-top">
                            <ActivityBar {...activityBarProps} inline={true} />
                        </div>
                        <div className="clinical-day-view-main">
                            {children}
                        </div>
                    </div>
                ) : (
                    children
                )}
            </main>

            {/* Add Patient modal (Phase 5: moved from AppHeader, triggered by addPatient action chip) */}
            <AddEditPatient
                setPatients={null}
                isEditPatientOpen={isEditPatientOpen}
                setEditPatientOpen={handleSetEditPatientOpen}
                patientId={editPatientId}
                onPatientArchived={() => {
                    closeActivityBar();
                    navigate("/dashboard");
                }}
            />

            {/* Floating popups for priority notifications (errors by default). */}
            <PriorityNotificationPopup />
        </div>
    );
};

/**
 * AppShell provides AppShellContext to inner components.
 */
const AppShell = ({ children, activityBarPosition, activityBarDismissed }) => {
    const { currentUser } = useAuth();

    return (
        <AppShellProvider
            userId={currentUser?.uid}
            initialActivityBarPosition={activityBarPosition}
            initialActivityBarDismissed={activityBarDismissed}
        >
            <ActiveJobsProvider userId={currentUser?.uid}>
                <UnifiedNotificationProvider userId={currentUser?.uid}>
                    <AppShellInner>
                        {children}
                    </AppShellInner>
                </UnifiedNotificationProvider>
            </ActiveJobsProvider>
        </AppShellProvider>
    );
};

export default AppShell;
